import {
  Box,
  Card,
  Flex,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Stack,
  StackSeparator,
  useDisclosure,
  Button,
  Editable,
  EditablePreview,
  EditableInput,
} from "@chakra-ui/react";
import { t } from "i18next";
import { ProjectPermissions } from "../../../../project/models/Project";
import {
  CheckIcon,
  CloseIcon,
  ExpandIcon,
  ResizeIcon,
} from "../../../../shared/views/icons";
import { CustomDialog } from "../../../../shared/views/CustomDialog";
import { CustomMenu } from "../../../../shared/views/CustomMenu";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Props {
  id: string;
  name: string | null;
  defaultName: string;
  colSpan: number;
  children: React.ReactNode;
  projectPermissions?: ProjectPermissions;
  onRename: (value: string | null) => void;
  onDelete: () => void;
  onResize: (colSpan: number) => void;
}

export const ChartWrapper: React.FC<Props> = ({
  id,
  name,
  defaultName,
  colSpan,
  children,
  projectPermissions,
  onRename,
  onDelete,
  onResize,
}) => {
  const expandModal = useDisclosure();

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
    touchAction: "none",
  };
  return (
    <GridItem colSpan={colSpan}>
      <div ref={setNodeRef} style={style}>
        <Card.Root variant="outline">
          <Stack separator={<StackSeparator />} gap={0}>
            <HStack p={2}>
              <Editable.Root
                key={name}
                disabled={!projectPermissions?.canManageResults}
                placeholder={defaultName}
                flexGrow={1}
                px={2}
                fontSize="sm"
                defaultValue={name || ""}
                onValueCommit={({ value }) => onRename(value.trim() || null)}
              >
                <EditablePreview />
                <EditableInput />
              </Editable.Root>
              <IconButton
                size="sm"
                variant="ghost"
                colorPalette="primary"
                aria-label="Expand"
                onClick={expandModal.onOpen}
                data-html2canvas-ignore="true"
              >
                <Icon as={ExpandIcon} />
              </IconButton>

              {projectPermissions?.canManageResults && (
                <>
                  <CustomMenu
                    items={itemSizes.map((item) => ({
                      icon:
                        item.colSpan === colSpan ? (
                          <Icon color={"primary.600"} as={CheckIcon} />
                        ) : undefined,
                      onClick: () => onResize(item.colSpan),
                      text: t(`map:dashboard.size.${item.size}`),
                      key: `${item.size}`,
                    }))}
                  >
                    <IconButton
                      size="sm"
                      variant="ghost"
                      colorPalette="primary"
                      data-html2canvas-ignore="true"
                    >
                      <Icon as={ResizeIcon} />
                    </IconButton>
                  </CustomMenu>

                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorPalette="red"
                    onClick={onDelete}
                    aria-label="Delete"
                    data-html2canvas-ignore="true"
                  >
                    <Icon as={CloseIcon} />
                  </IconButton>

                  <Box
                    {...attributes}
                    {...listeners}
                    cursor="grab"
                    _active={{ cursor: "grabbing" }}
                    px={1}
                    color="gray.400"
                    _hover={{ color: "gray.600" }}
                    fontSize="lg"
                    lineHeight="1"
                    userSelect="none"
                    data-html2canvas-ignore="true"
                  >
                    ⋮⋮
                  </Box>
                </>
              )}
            </HStack>
            <Flex overflow="auto" maxHeight="400px">
              <Box flexGrow={1}>{!expandModal.open && children}</Box>
            </Flex>
          </Stack>
        </Card.Root>
      </div>

      {expandModal.open && (
        <CustomDialog
          onClose={expandModal.onClose}
          size="full"
          header={name || defaultName}
          footer={
            <Button colorPalette="primary" onClick={expandModal.onClose}>
              {t("shared:close")}
            </Button>
          }
        >
          {children}
        </CustomDialog>
      )}
    </GridItem>
  );
};

const itemSizes = [
  { size: "sm", colSpan: 2 },
  { size: "md", colSpan: 3 },
  { size: "lg", colSpan: 4 },
  { size: "xl", colSpan: 6 },
];
